import React, { useState, useEffect, useRef } from "react";
import { useVideos } from "../hooks/useApi";
import type { Video } from "../types/api";
import { isImageUrl } from "../utils/videoUtils";

interface VideoSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVideo: (video: Video) => void;
}

const VideoSelectionModal: React.FC<VideoSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectVideo,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedVideos, setAllLoadedVideos] = useState<Video[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [hoveredVideoId, setHoveredVideoId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  
  // Using real API call
  const { videos, loading, error, refetch } = useVideos(currentPage, 12);
  
  // Check if container is scrollable
  const isScrollable = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return false;
    return container.scrollHeight > container.clientHeight;
  }, []);
  
  // Load next page
  const loadNextPage = React.useCallback(() => {
    if (videos && videos.pagination.has_next && !isLoadingMore && !loading) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  }, [videos, isLoadingMore, loading]);

  // Fuzzy search function - matches prompts with typo tolerance
  const fuzzySearch = (text: string, query: string): boolean => {
    if (!query.trim()) return true;

    const normalizedText = text.toLowerCase();
    const normalizedQuery = query.toLowerCase().trim();
    const queryWords = normalizedQuery.split(/\s+/);

    // Check if all query words appear in the text (in order or out of order)
    return queryWords.every((word) => normalizedText.includes(word));
  };

  // Reset to page 1 when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setAllLoadedVideos([]);
      setSearchQuery("");
      setShowFavoritesOnly(false);
      setHoveredVideoId(null);
    }
  }, [isOpen]);

  // Load videos for current page
  useEffect(() => {
    if (isOpen && videos && videos.videos.length > 0) {
      setAllLoadedVideos((prev) => {
        // Get new videos that aren't already loaded
        const newVideos = videos.videos.filter(
          (video) => !prev.some((v) => v.id === video.id)
        );
        if (newVideos.length > 0) {
          return [...prev, ...newVideos];
        }
        return prev;
      });
      setIsLoadingMore(false);
    }
  }, [isOpen, videos]);

  // Prefill until scrollable (or done) - bootstrap on open/resize
  useEffect(() => {
    if (!isOpen || !videos || loading || isLoadingMore) return;
    if (searchQuery.trim() || showFavoritesOnly) return; // Skip when filtering
    
    const checkAndPrefill = async () => {
      // Wait for DOM to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (isScrollable()) return; // Already scrollable, done
      if (!videos.pagination.has_next) return; // No more data
      
      const maxPrefetches = 3;
      let prefetches = 0;
      
      while (prefetches < maxPrefetches) {
        if (isScrollable()) break;
        if (!videos.pagination.has_next) break;
        if (isLoadingMore || loading) break;
        
        loadNextPage();
        prefetches++;
        
        // Wait for next page to load
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    };
    
    checkAndPrefill();
  }, [isOpen, videos, allLoadedVideos.length, searchQuery, showFavoritesOnly, isScrollable, loadNextPage, isLoadingMore, loading]);
  
  // Also check on window resize
  useEffect(() => {
    if (!isOpen) return;
    
    const handleResize = () => {
      if (isScrollable()) return;
      if (!videos?.pagination.has_next) return;
      if (searchQuery.trim() || showFavoritesOnly) return;
      
      // Small delay to let resize complete
      setTimeout(() => {
        if (!isScrollable() && videos?.pagination.has_next && !isLoadingMore && !loading) {
          loadNextPage();
        }
      }, 200);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, videos?.pagination, allLoadedVideos.length, searchQuery, showFavoritesOnly, isLoadingMore, loading, isScrollable, loadNextPage]);

  // Sentinel + IntersectionObserver - load next page when sentinel enters view
  useEffect(() => {
    if (!isOpen) return;
    if (searchQuery.trim() || showFavoritesOnly) return; // Skip when filtering
    
    const sentinel = sentinelRef.current;
    if (!sentinel || !videos) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && videos.pagination.has_next && !isLoadingMore && !loading) {
          loadNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px', // Start loading slightly before sentinel is visible
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, videos, isLoadingMore, loading, searchQuery, showFavoritesOnly, loadNextPage]);

  if (!isOpen) return null;

  // Helper function to extract user prompt from combined prompt for display
  const extractUserPrompt = (combinedPrompt: string): string => {
    if (!combinedPrompt) return "";

    // Check for old format with explicit USER CONTENT delimiter
    const userContentDelimiter = "USER CONTENT: ";
    if (combinedPrompt.includes(userContentDelimiter)) {
      const userContentStart =
        combinedPrompt.indexOf(userContentDelimiter) +
        userContentDelimiter.length;
      const userContentEnd = combinedPrompt.indexOf("\n", userContentStart);

      if (userContentEnd !== -1) {
        return combinedPrompt
          .substring(userContentStart, userContentEnd)
          .trim();
      } else {
        return combinedPrompt.substring(userContentStart).trim();
      }
    }

    // For new format, extract main content before technical instructions
    const technicalPattern = /\.\s+(shot with|filmed in)/i;
    const match = combinedPrompt.match(technicalPattern);

    if (match) {
      return combinedPrompt.substring(0, match.index).trim();
    }

    // If no technical instructions found, return the full prompt
    return combinedPrompt.trim();
  };

  const completedVideos =
    allLoadedVideos
      .filter((video) => video.status === "completed" && video.url)
      .filter((video) => {
        // Filter by favorites if enabled
        if (showFavoritesOnly && !video.favorite) {
          return false;
        }
        // Search filter
        if (!searchQuery.trim()) return true;
        // Search in both the full prompt and the extracted user prompt
        const fullPrompt = video.prompt || "";
        const userPrompt = extractUserPrompt(fullPrompt);
        return (
          fuzzySearch(fullPrompt, searchQuery) ||
          fuzzySearch(userPrompt, searchQuery)
        );
      })

  const handleSelectVideo = (video: Video) => {
    onSelectVideo(video);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-black border border-white/20 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/20 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Link Existing Asset
            </h2>
            <p className="text-sm text-white/60 mt-1">
              Select an image or select a video frame to use as reference for
              your new generation
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg
              className="w-6 h-6 text-white/60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search Bar and Filters */}
        <div className="px-6 pt-6 pb-4 border-b border-white/20 flex-shrink-0">
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`px-4 py-2 border font-medium rounded-lg transition-colors flex items-center gap-2 text-sm ${
                showFavoritesOnly
                  ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/30"
                  : "border-white/20 text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <svg
                fill={showFavoritesOnly ? "currentColor" : "none"}
                stroke="currentColor"
                viewBox="0 0 24 24"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={showFavoritesOnly ? 0 : 2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
              {showFavoritesOnly ? "Show All" : "Favorites"}
            </button>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assets by prompt..."
              className="block w-full pl-10 pr-3 py-2 border border-white/20 rounded-lg leading-5 bg-black/50 backdrop-blur-sm placeholder-white/40 focus:outline-none focus:placeholder-white/60 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 text-sm text-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-white/10 rounded transition-colors"
              >
                <svg
                  className="h-5 w-5 text-white/40 hover:text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div
          ref={scrollContainerRef}
          className="p-6 overflow-y-auto flex-1 min-h-0 overflow-x-hidden"
          style={{ maxHeight: '100%' }}
        >
          {loading && !videos ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-white/60">Loading your videos...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
                <svg
                  className="w-6 h-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Failed to Load Videos
              </h3>
              <p className="text-white/60 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="px-4 py-2 bg-blue-500/80 text-white font-medium rounded-lg hover:bg-blue-500 transition-colors backdrop-blur-sm border border-blue-500/30"
              >
                Try Again
              </button>
            </div>
          ) : completedVideos.length === 0 ? (
            <div className="text-center py-12">
              {searchQuery ? (
                <>
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20">
                    <svg
                      className="w-6 h-6 text-white/60"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    No videos found
                  </h3>
                  <p className="text-white/60 mb-4">
                    No videos match your search for &quot;{searchQuery}&quot;
                  </p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
                    <svg
                      className="w-6 h-6 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    No Completed Videos
                  </h3>
                  <p className="text-white/60">
                    You don't have any completed videos yet. Create some videos
                    first to use this feature.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Videos Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {completedVideos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => handleSelectVideo(video)}
                    className="bg-black/50 backdrop-blur-sm rounded-lg border border-white/20 overflow-visible hover:shadow-lg hover:border-blue-500/50 hover:shadow-blue-500/10 transition-all duration-200 cursor-pointer group relative"
                  >
                    {/* Media Thumbnail */}
                    <div className="aspect-video bg-gray-900 relative overflow-hidden">
                      {isImageUrl(video.url || "") ? (
                        <img
                          src={video.url}
                          alt={`AI generated content: ${video.prompt}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={video.url}
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          playsInline
                          webkit-playsinline="true"
                          onError={(e) => {
                            console.error(
                              "VideoSelection video failed to load:",
                              e
                            );
                            console.error("Video URL:", video.url);
                          }}
                          onLoadedData={() => {
                            console.log(
                              "VideoSelection video loaded:",
                              video.id
                            );
                          }}
                        />
                      )}
                      {/* Play overlay (only for videos) */}
                      {!isImageUrl(video.url || "") && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                          <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <svg
                              className="w-6 h-6 text-gray-700 ml-1"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* Video icon indicator (top left) */}
                      {!isImageUrl(video.url || "") && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className="w-5 h-5 bg-black/80 backdrop-blur-sm rounded flex items-center justify-center text-white/90 border border-white/20 shadow-sm">
                            <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              className="w-3.5 h-3.5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                      {/* Info icon */}
                      <div
                        className="absolute top-2 right-2 z-10"
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => setHoveredVideoId(video.id)}
                        onMouseLeave={() => setHoveredVideoId(null)}
                      >
                        <div className="w-5 h-5 bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white/90 hover:text-white border border-white/20 cursor-default shadow-sm">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            className="w-3.5 h-3.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      {/* Date overlay */}
                      <div className="absolute bottom-2 left-2 z-10">
                        <div className="bg-black/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded border border-white/20">
                          {formatDate(video.created_at)}
                        </div>
                      </div>
                    </div>
                    {/* Tooltip - positioned outside the preview container */}
                    {hoveredVideoId === video.id && (
                      <div className="absolute top-2 right-2 z-50 pointer-events-none">
                        <div className="bg-black/90 backdrop-blur-md text-white text-xs rounded-lg py-2 px-3 shadow-xl border border-white/20 w-64 -translate-y-full mb-2">
                          <p className="whitespace-normal break-words">
                            {extractUserPrompt(video.prompt) || video.prompt}
                          </p>
                          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/90"></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Sentinel - triggers IntersectionObserver */}
              {!searchQuery.trim() && !showFavoritesOnly && (
                <div ref={sentinelRef} className="h-1 w-full" />
              )}

              {/* Loading More Indicator */}
              {isLoadingMore && videos && videos.pagination.has_next && (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-sm text-white/60">
                    Loading more videos...
                  </span>
                </div>
              )}

              {/* Fallback button - show if no scroll but has more */}
              {(() => {
                const scrollable = isScrollable();
                return (
                  !isLoadingMore &&
                  !loading &&
                  videos &&
                  videos.pagination.has_next &&
                  !scrollable &&
                  !searchQuery.trim() &&
                  !showFavoritesOnly &&
                  completedVideos.length > 0 && (
                    <div className="text-center py-4">
                      <button
                        onClick={loadNextPage}
                        className="px-4 py-2 border border-white/20 text-white/80 font-medium rounded-lg hover:bg-white/10 hover:border-white/40 transition-all text-sm"
                      >
                        Load More
                      </button>
                    </div>
                  )
                );
              })()}

              {/* End of list indicator */}
              {!isLoadingMore &&
                videos &&
                !videos.pagination.has_next &&
                completedVideos.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-xs text-white/40">
                      You've reached the end of your assets
                    </p>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoSelectionModal;
